import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { HttpService } from 'services/http.service';
import { ServiceBase } from 'services/servicebase';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApprovalScopeModel } from 'services/models/corporate/approval-scope/approval-scope.model';
import { Response } from 'services/models/response';
import { ApprovalActionModel } from 'services/models/corporate/approval-scope/approval-action.model';
import { ApprovalScopeCustomFieldModel } from 'services/models/corporate/approval-scope/approval-scope-custom-field.model';
import {
  ApprovalScopeStandardFieldValidationConfigModel,
  StandardFieldValidationCatalogModel,
  StandardFieldValidationResolvedModel,
} from 'services/models/corporate/approval-scope/standard-field-validation.model';
import { ApprovalScopeOpeningInstructionsContextModel } from 'services/models/corporate/approval-scope/approval-scope-opening-instructions-context.model';
import { ApprovalScopeStepInstructionsContextModel } from 'services/models/corporate/approval-scope/approval-scope-step-instructions-context.model';
import { MeritPromotionSettingsModel } from 'services/models/corporate/approval-scope/merit-promotion-settings.model';
import { QueryParams } from 'services/models/odata/queryParams';
import { ApprovalScopeItemApproversModel } from 'services/models/corporate/approval-scope/approval-scope-item-approvers.model';
import { ElegibleApproverModel } from 'services/models/corporate/approval-scope/elegible-approver.model';
import { StandardFieldVisibilityModel } from 'services/models/corporate/approval-scope/standard-field-visibility.model';

@Injectable()
export class ApprovalScopeService extends ServiceBase<ApprovalScopeModel> {
  constructor(http: HttpService) {
    super('ApprovalScope', http, environment.apiUrl);
  }

  createEntity(input: any): ApprovalScopeModel {
    return ApprovalScopeModel.create(input);
  }

  getById(id: string, disableLoading = false): Observable<Response<ApprovalScopeModel>> {
    return this.get(id, disableLoading);
  }

  createOrUpdate(model: ApprovalScopeModel): Observable<Response<ApprovalScopeModel>> {
    return this.save(model.id || '', model);
  }

  deleteScope(id: string): Observable<Response<any>> {
    return this.delete(id);
  }

  getApprovalActionsByCompanyRoot(
    companyRootId: string,
    params?: QueryParams
  ): Observable<ApprovalActionModel[]> {
    const queryParams = this._buildApprovalActionsQueryParams(params);
    const odataQuery = this.createBuildQuery(queryParams);
    const odataQueryWithAmpersand = odataQuery.replace(/^\?/, '&');
    const endpoint = `${this.endpoint}/approval-actions/odata?companyRootId=${companyRootId}${odataQueryWithAmpersand}`;

    return this.http
      .get(endpoint, null, this.baseUrl, null, true)
      .pipe(map(res => (res.body || []).map((item: any) => ApprovalActionModel.create(item))));
  }

  getApprovalActionsByMovementType(
    params?: QueryParams,
    movementType?: string
  ): Observable<ApprovalActionModel[]> {
    const queryParams = this._buildApprovalActionsQueryParams(params);
    const odataQuery = this.createBuildQuery(queryParams);
    const odataQueryWithAmpersand = odataQuery.replace(/^\?/, '&');
    const movementTypeQuery = movementType ? `movementType=${movementType}` : '';
    const separator = movementTypeQuery && odataQueryWithAmpersand ? '&' : '';
    const endpoint = `${this.endpoint}/approval-actions/odata?${movementTypeQuery}${separator}${odataQueryWithAmpersand.replace(
      /^&/,
      ''
    )}`;

    return this.http
      .get(endpoint, null, this.baseUrl, null, true)
      .pipe(map(res => (res.body || []).map((item: any) => ApprovalActionModel.create(item))));
  }

  private _buildApprovalActionsQueryParams(params?: QueryParams): QueryParams {
    // Se não foram passados QueryParams, criar um padrão
    const queryParams = params || new QueryParams();

    // Configurar valores padrão se não foram fornecidos
    if (!params) {
      queryParams.select = queryParams.select || [
        'id',
        'name',
        'description',
        'actionType',
        'source',
      ];
      queryParams.orderBy = queryParams.orderBy || 'name';
      queryParams.orderByOrder = queryParams.orderByOrder || 'asc';
    }

    return queryParams;
  }

  getOpeningInstructionsContext(companyId: string, movementType: number): Observable<ApprovalScopeOpeningInstructionsContextModel> {
    const endpoint = `${this.endpoint}/opening-instructions/context?companyId=${companyId}&movementType=${movementType}`;
    return this.http.get(endpoint, null, this.baseUrl, null, true).pipe(
      map((res: any) =>
        ApprovalScopeOpeningInstructionsContextModel.create(res?.body ?? res)
      )
    );
  }

  hideOpeningInstructions(approvalScopeId: string): Observable<unknown> {
    const endpoint = `${this.endpoint}/${approvalScopeId}/opening-instructions/hide-preference`;
    return this.http.post(endpoint, {}, null, null, true);
  }

  getStepInstructionsContext(params: {
    approvalScopeItemId?: string;
    companyId?: string;
    movementType?: number;
    sequence?: number;
  }): Observable<ApprovalScopeStepInstructionsContextModel> {
    const query = new URLSearchParams();

    if (params.approvalScopeItemId) {
      query.set('approvalScopeItemId', params.approvalScopeItemId);
    }
    if (params.companyId) {
      query.set('companyId', params.companyId);
    }
    if (params.movementType != null) {
      query.set('movementType', String(params.movementType));
    }
    if (params.sequence != null) {
      query.set('sequence', String(params.sequence));
    }

    const endpoint = `${this.endpoint}/step-instructions/context?${query.toString()}`;
    return this.http.get(endpoint, null, this.baseUrl, null, true).pipe(
      map((res: any) =>
        ApprovalScopeStepInstructionsContextModel.create(res?.body ?? res)
      )
    );
  }

  public proccessApproversPreview(
    obj: ApprovalScopeItemApproversModel
  ): Observable<ElegibleApproverModel[]> {
    return this.http
      .post(
        `${this.endpoint}/eligible-approvers/preview`,
        JSON.stringify(obj),
        null,
        null,
        true
      )
      .pipe(
        map(res => {
          const items = Array.isArray(res) ? res : (res as { body?: unknown })?.body;
          const list = Array.isArray(items) ? items : [];
          return list.map((item: any) => ElegibleApproverModel.create(item));
        })
      );
  }

  public listCustomFields(includeInactive: boolean = true): Observable<ApprovalScopeCustomFieldModel[]> {
    return this.http
      .get(
        `${this.endpoint}/custom-fields?includeInactive=${includeInactive}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => ApprovalScopeCustomFieldModel.create(item))
        )
      );
  }

  public saveCustomField(
    model: ApprovalScopeCustomFieldModel
  ): Observable<ApprovalScopeCustomFieldModel> {
    const payload: any = {
      ...model,
      options: model.options || [],
    };

    if (!payload.fieldId) {
      delete payload.fieldId;
    }

    if (!payload.id) {
      delete payload.id;
    }

    return this.http
      .post(`/${this.endpoint}/custom-fields`, payload, null, this.baseUrl, true)
      .pipe(map((res: any) => ApprovalScopeCustomFieldModel.create(res.body)));
  }

  public listResolvedCustomFields(
    approvalScopeItemId: string
  ): Observable<ApprovalScopeCustomFieldModel[]> {
    return this.http
      .get(
        `${this.endpoint}/custom-fields/resolved/${approvalScopeItemId}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => ApprovalScopeCustomFieldModel.create(item))
        )
      );
  }

  public listMovementTypeCustomFields(
    movementType: number | string,
    companyId?: string
  ): Observable<ApprovalScopeCustomFieldModel[]> {
    return this.http
      .get(
        `${this.endpoint}/custom-fields/by-movement-type/${movementType}`,
        null,
        this.baseUrl,
        companyId ? { companyId } : null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => ApprovalScopeCustomFieldModel.create(item))
        )
      );
  }

  public getMeritPromotionSettings(companyId: string): Observable<MeritPromotionSettingsModel> {
    return this.http
      .get(
        `${this.endpoint}/merit-promotion-settings/${companyId}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(map((res: any) => MeritPromotionSettingsModel.create(res.body)));
  }

  public saveMeritPromotionSettings(model: MeritPromotionSettingsModel): Observable<MeritPromotionSettingsModel> {
    return this.http
      .post(
        `/${this.endpoint}/merit-promotion-settings`,
        model,
        null,
        this.baseUrl,
        true
      )
      .pipe(map((res: any) => MeritPromotionSettingsModel.create(res.body)));
  }

  public listStandardFieldValidationCatalog(
    movementType: number | string
  ): Observable<StandardFieldValidationCatalogModel[]> {
    return this.http
      .get(
        `${this.endpoint}/standard-field-validations/catalog?movementType=${movementType}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => StandardFieldValidationCatalogModel.create(item))
        )
      );
  }

  public listResolvedStandardFieldValidations(
    approvalScopeItemId: string
  ): Observable<StandardFieldValidationResolvedModel[]> {
    return this.http
      .get(
        `${this.endpoint}/standard-field-validations/resolved/${approvalScopeItemId}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => StandardFieldValidationResolvedModel.create(item))
        )
      );
  }

  public saveStandardFieldValidations(
    approvalScopeId: string,
    approvalScopeItemId: string,
    items: ApprovalScopeStandardFieldValidationConfigModel[]
  ): Observable<unknown> {
    const payload = {
      approvalScopeId,
      approvalScopeItemId,
      items: (items || []).map(item => ({
        validationKey: item.validationKey,
        enabled: item.enabled,
        behavior: item.behavior,
        alertMessage: item.alertMessage || '',
      })),
    };

    return this.http.post(
      `/${this.endpoint}/standard-field-validations/${approvalScopeItemId}`,
      payload,
      null,
      this.baseUrl,
      true
    );
  }

  public listStandardFieldVisibility(approvalScopeItemId: string): Observable<StandardFieldVisibilityModel[]> {
    return this.http
      .get(
        `${this.endpoint}/standard-field-visibility/${approvalScopeItemId}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) =>
          (res.body || []).map((item: any) => StandardFieldVisibilityModel.create(item))
        )
      );
  }

  public saveStandardFieldVisibility(
    approvalScopeId: string,
    approvalScopeItemId: string,
    hiddenFieldKeys: string[]
  ): Observable<unknown> {
    return this.http.post(
      `/${this.endpoint}/standard-field-visibility/${approvalScopeItemId}`,
      { approvalScopeId, approvalScopeItemId, hiddenFieldKeys: hiddenFieldKeys || [] },
      null,
      this.baseUrl,
      true
    );
  }

  public listHiddenStandardFieldsByMovementType(
    movementType: number | string,
    companyId?: string
  ): Observable<string[]> {
    return this.http
      .get(
        `${this.endpoint}/standard-field-visibility/hidden/by-movement-type/${movementType}`,
        null,
        this.baseUrl,
        companyId ? { companyId } : null,
        true
      )
      .pipe(map((res: any) => (res.body || []) as string[]));
  }
}
